import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../utility/axiosInstance";

export interface Database {
    id: number;
    name: string;
    db_name: string;
    db_user: string;
    db_version: string;
    status: "provisioning" | "ready" | "error";
    floating_ip: string | null;
    port: number;
    instance_id: string;
    created_at: string;
    access_point?: string | null;
    private_ip?: string | null;
    // High availability. Optional because a row created before HA shipped, or an API build that
    // does not select these columns yet, simply omits them - and "absent" is not the same as
    // "false", so the UI must be able to tell the two apart rather than assuming single-machine.
    // `vip` is the address the customer connects to on an HA database: a member's own IP is wrong
    // the moment that member is rebuilt. `member_ips` is however the API serialises the member
    // list (a JSON array or a comma-separated string), so parse it defensively.
    ha?: boolean;
    vip?: string | null;
    member_ips?: string | string[] | null;
    // An upgrade-in-place is running RIGHT NOW. Derived server-side (and bounded by the workflow's
    // own run timeout, so it cannot stick after a dead worker) rather than computed here: a second
    // copy of "is it still going?" in the console would disagree with the API's copy eventually,
    // and the disagreement would show as a button that is offered while an upgrade is underway.
    ha_upgrade_in_progress?: boolean;
    /** The machine size, so the upgrade modal can price the change for real. */
    flavor_id?: string | null;
    // The data view (infrastructure/db-helper/DESIGN_V1.md D6). ABSENT when the data view is off for this account (the
    // API leaves it out, so the console offers nothing); false when this database has no working helper (made before
    // the data view, or its setup is still running or failed); true when its data can be read. Absent is not false.
    data_view?: boolean;
    /** DESIGN_EDITING.md E15: present whenever data_view is; true when this database's helper can export. */
    data_export?: boolean;
    /** E15: absent unless editing is on for the account; true when this database can be edited. */
    data_edit?: boolean;
    /** E27: present whenever data_view is; true when this database's helper can show related rows. */
    data_related?: boolean;
    /** E28: present with data_edit; true while editing is locked on this database (the server refuses every write). */
    data_edit_locked?: boolean;
    // shown once on creation only
    db_password?: string;
}

interface DatabasesState {
    databases: Database[];
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: DatabasesState = { databases: [], status: "idle", error: null };

export const fetchDatabases = createAsyncThunk("databases/fetch", async () => {
    const res = await axiosInstance.get("/v1/databases");
    return res.data as Database[];
});

export const deleteDatabase = createAsyncThunk("databases/delete", async (id: number) => {
    await axiosInstance.delete(`/v1/databases/${id}`);
    return id;
});

const databasesSlice = createSlice({
    name: "databases",
    initialState,
    reducers: {
        addDatabase: (state, action) => { state.databases.unshift(action.payload); },
        updateDatabaseStatus: (state, action) => {
            const db = state.databases.find(d => d.id === action.payload.id);
            if (db) { db.status = action.payload.status; db.floating_ip = action.payload.floating_ip ?? db.floating_ip; }
        },
        // A fresh read of one row's data_view; `undefined` removes the field (the data view went off for this account).
        updateDatabaseDataView: (state, action: PayloadAction<{ id: number; data_view: boolean | undefined }>) => {
            const db = state.databases.find(d => d.id === action.payload.id);
            if (!db) return;
            if (action.payload.data_view === undefined) delete db.data_view;
            else db.data_view = action.payload.data_view;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDatabases.pending,   (s) => { s.status = "loading"; })
            .addCase(fetchDatabases.fulfilled, (s, a) => { s.status = "succeeded"; s.databases = a.payload; })
            .addCase(fetchDatabases.rejected,  (s, a) => { s.status = "failed"; s.error = a.error.message ?? null; })
            .addCase(deleteDatabase.fulfilled, (s, a) => { s.databases = s.databases.filter(d => d.id !== a.payload); });
    },
});

export const { addDatabase, updateDatabaseStatus, updateDatabaseDataView } = databasesSlice.actions;
export default databasesSlice.reducer;
